import React from 'react';

// Stage 1: the concept itself, as an editorial read.
//
// This used to be a permanent left-hand column, so the objectives occupied half the screen
// while someone worked on pricing six sections away. It is a stage now: read it to orient,
// then move on, and come back in one click.
//
// It also renders fields the payload has always carried but the page never showed -
// course_purpose, course_structure, interaction_check, completion_standard, market_signal
// and the recommended length. Nothing was dropped in the move; these were added.
const OBJECTIVE_STEM = /^\s*learners?\s+will\s+be\s+able\s+to:?\s*/i;
const objectiveText = (o) => String(o?.text ?? o ?? '').replace(OBJECTIVE_STEM, '');

const Rule = () => <div className="h-px bg-[#e2d7c3] my-4" />;
const Label = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wide text-[#9a8a6d] mb-1">{children}</div>
);

const minutes = (m) => {
  if (!m) return null;
  return m >= 60 && m % 60 === 0 ? `${m / 60}h` : `${m} min`;
};

export default function ConceptStage({ concept: c }) {
  const structure = (c.course_structure || []).map((s) => s?.section ?? s).filter(Boolean);
  const len = minutes(c.recommended_length_minutes);

  return (
    <div className="rounded-2xl bg-[#faf7f2] text-[#2b2620] border border-[#e7ddcb] p-6"
      style={{ fontFamily: 'Georgia, serif' }}>
      <div className="text-[10px] tracking-widest uppercase text-[#a1783c]">
        {[c.program, c.learning_level, c.delivery_recommendation, len].filter(Boolean).join(' · ')}
      </div>
      <h1 className="text-2xl font-semibold my-2 leading-tight">{c.title}</h1>
      <p className="text-sm text-[#4f473b] leading-relaxed">{c.summary_text}</p>

      {c.course_purpose && (
        <>
          <Rule />
          <Label>Course purpose</Label>
          <div className="text-sm text-[#4f473b] leading-relaxed">{c.course_purpose}</div>
        </>
      )}

      <Rule />
      <Label>Profitability / affordability</Label>
      <div className="text-sm text-[#4f473b] leading-relaxed">{c.profitability_affordability_connection}</div>

      <Rule />
      <Label>Objectives</Label>
      {(c.objectives || []).length > 0 && <div className="text-sm mb-1">Learners will be able to:</div>}
      {(c.objectives || []).map((o, i) => (
        <div key={i} className="text-sm my-1.5 pl-4 relative">
          <span className="absolute left-0 text-[#a1783c]">→</span>
          {objectiveText(o)}
        </div>
      ))}
      {(c.objectives || []).length === 0 && (
        <div className="text-sm text-[#8a7f6d]">No objectives yet.</div>
      )}

      {structure.length > 0 && (
        <>
          <Rule />
          <Label>Course structure</Label>
          {structure.map((s, i) => (
            <div key={i} className="text-sm my-1.5 pl-5 relative">
              <span className="absolute left-0 text-[#a1783c] text-xs">{i + 1}.</span>
              {s}
            </div>
          ))}
        </>
      )}

      {(c.interaction_check || c.completion_standard) && (
        <>
          <Rule />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.interaction_check && (
              <div>
                <Label>Interaction check</Label>
                <div className="text-sm text-[#4f473b]">{c.interaction_check}</div>
              </div>
            )}
            {c.completion_standard && (
              <div>
                <Label>Completion standard</Label>
                <div className="text-sm text-[#4f473b]">{c.completion_standard}</div>
              </div>
            )}
          </div>
        </>
      )}

      {c.market_signal && (
        <>
          <Rule />
          <Label>Market signal</Label>
          <div className="text-sm text-[#4f473b] leading-relaxed">{c.market_signal}</div>
        </>
      )}

      {(c.needs_revision_reasons || []).length > 0 && (
        <div className="mt-4 text-xs bg-[#fde9d6] text-[#9a5b12] rounded-lg px-3 py-2">
          ⚠ Needs revision: {c.needs_revision_reasons.join('; ')}
        </div>
      )}
    </div>
  );
}
